import type { DrawingDocument, DrawingEntity, ErrorCode, QualityIssue } from '../contract/types.js';
import { measureSheet } from '../transform/scale.js';
import {
  DEFECT_SEVERITY,
  RE_EXPORT_HELPS,
  bySeverity,
  type Defect,
  type DefectKind,
} from './defects.js';

/**
 * Inspecting a drawing before it is converted.
 *
 * **"Before converting" means before the DXF, not before the read.** Extraction
 * is how this pipeline sees inside a PDF at all — whether text is outlines,
 * whether a page is a picture, whether a curve is an arc — so everything here
 * is derived from what extraction already reconciled rather than from a second
 * pass over the bytes. Nothing is re-measured, which is what keeps the report
 * and the conversion describing the same file.
 *
 * A file that never opened has no document to inspect; {@link validateFailure}
 * turns that outcome into the same shape.
 */

export interface ValidationReport {
  /**
   * `ready` — convert it.
   * `degraded` — a DXF will be produced and something named will be missing.
   * `refused` — no DXF at all.
   */
  verdict: 'ready' | 'degraded' | 'refused';
  defects: Defect[];
  /**
   * Whether exporting again with different settings would change any of this.
   *
   * The invitation to re-export is only worth making when it is true, and this
   * is the whole reason a defect records it: telling somebody to try again on a
   * drawing that genuinely contains a spline wastes their afternoon and teaches
   * them to ignore the next message.
   */
  reExportWouldHelp: boolean;
}

function defect(kind: DefectKind, count: number, measured: string, pageIndex?: number, bbox?: readonly [number, number, number, number]): Defect {
  return {
    kind,
    severity: DEFECT_SEVERITY[kind],
    count,
    measured,
    ...(pageIndex === undefined ? {} : { pageIndex }),
    ...(bbox === undefined ? {} : { bbox }),
  };
}

function report(defects: Defect[]): ValidationReport {
  const sorted = [...defects].sort(bySeverity);
  const worst = sorted[0]?.severity;
  return {
    verdict: worst === 'blocking' ? 'refused' : worst === undefined ? 'ready' : 'degraded',
    defects: sorted,
    reExportWouldHelp: sorted.some((d) => RE_EXPORT_HELPS[d.kind]),
  };
}

/** Quality issues that are notes about the pipeline rather than facts about the file. */
function isRasterPaint(issue: QualityIssue): boolean {
  return issue.kind === 'unsupported_operator' && issue.operator === 'paintImage';
}

/**
 * The write-order half of `merge_candidate`, and only that half.
 *
 * The same quality issue covers two situations: a spacing the rule found
 * genuinely marginal, and a pair the rule *would* have merged had the producer
 * not written their runs far apart in the content stream. Only the second is
 * evidence about the producer, and reporting both as a producer risk cries wolf
 * on every drawing that has one-and-a-half-spaced text in it. See finding 9.
 */
const WROTE_THEM_APART = /wrote them \d+ runs apart/;

function sum(issues: readonly QualityIssue[], predicate: (i: QualityIssue) => boolean): number {
  return issues.filter(predicate).reduce((n, i) => n + i.count, 0);
}

function boxOf(entity: DrawingEntity): readonly [number, number, number, number] {
  switch (entity.type) {
    case 'LINE':
      return [
        Math.min(entity.start.x, entity.end.x),
        Math.min(entity.start.y, entity.end.y),
        Math.max(entity.start.x, entity.end.x),
        Math.max(entity.start.y, entity.end.y),
      ];
    case 'LWPOLYLINE': {
      const xs = entity.vertices.map((v) => v.x);
      const ys = entity.vertices.map((v) => v.y);
      return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    }
    case 'CIRCLE':
    case 'ARC':
      return [
        entity.centre.x - entity.radius,
        entity.centre.y - entity.radius,
        entity.centre.x + entity.radius,
        entity.centre.y + entity.radius,
      ];
    case 'TEXT':
    case 'MTEXT':
      return [entity.anchor.x, entity.anchor.y, entity.anchor.x, entity.anchor.y];
  }
}

/**
 * How far outside the page a mark has to sit before it is reported.
 *
 * A drawing frame drawn exactly on the page edge lands a hair outside it after
 * rounding, and a producer that rounds 841.89 to 841.9 puts the border 0.01 pt
 * over. One point is about a third of a millimetre: far below anything a person
 * drew on purpose and far above the arithmetic.
 */
export const OUTSIDE_PAGE_MARGIN_PT = 1;

export function validateDocument(doc: DrawingDocument): ValidationReport {
  const defects: Defect[] = [];
  const issues = doc.qualityIssues;

  // Every per-page defect below is raised inside the loop, so a document with no
  // pages skips all of them and falls through to the `worst === undefined`
  // branch — the friendliest verdict the tool has, with an empty defect list,
  // for a file carrying nothing at all. A page count of zero is the same finding
  // as a page with nothing on it, and is reported as such.
  if (doc.pages.length === 0) {
    defects.push(defect('no_content', 1, 'this file carries no pages', 0));
  }

  for (const page of doc.pages) {
    const rasterOps = sum(issues, (i) => isRasterPaint(i) && i.pageIndex === page.index);
    const hasGeometry = page.entities.length > 0;

    if (rasterOps > 0 && !hasGeometry) {
      // The commonest way a drawing arrives unusable: printed through a raster
      // driver, or scanned. There is no geometry to extract.
      defects.push(
        defect(
          'raster_only',
          rasterOps,
          `${rasterOps} raster image${rasterOps === 1 ? '' : 's'} and no vector geometry on this page`,
          page.index,
        ),
      );
    } else if (rasterOps > 0) {
      defects.push(
        defect(
          'raster_content',
          rasterOps,
          `${rasterOps} raster image${rasterOps === 1 ? '' : 's'} on a page that also carries ${page.entities.length} vector entities`,
          page.index,
        ),
      );
    } else if (!hasGeometry) {
      defects.push(defect('no_content', 1, 'this page carries no geometry and no image', page.index));
    }

    // The sheet is what physical scale is read from, so a page of no recognised
    // size is not a cosmetic complaint: an A3 treated as A4 converts at 71 % of
    // true size and looks entirely correct.
    const sheet = measureSheet(page.widthPt, page.heightPt);
    if (sheet.name === null) {
      defects.push(
        defect(
          'non_standard_sheet',
          1,
          `${sheet.widthMm.toFixed(1)} x ${sheet.heightMm.toFixed(1)} mm matches no A-series sheet`,
          page.index,
        ),
      );
    }

    const outside = page.entities.filter((e) => {
      const [x0, y0, x1, y1] = boxOf(e);
      return (
        x1 < -OUTSIDE_PAGE_MARGIN_PT ||
        y1 < -OUTSIDE_PAGE_MARGIN_PT ||
        x0 > page.widthPt + OUTSIDE_PAGE_MARGIN_PT ||
        y0 > page.heightPt + OUTSIDE_PAGE_MARGIN_PT
      );
    });
    if (outside.length > 0) {
      defects.push(
        defect(
          'content_outside_page',
          outside.length,
          `${outside.length} of ${page.entities.length} entities lie outside the ${page.widthPt.toFixed(0)} x ${page.heightPt.toFixed(0)} pt page`,
          page.index,
        ),
      );
    }
  }

  const outlined = issues.filter((i) => i.kind === 'outlined_text');
  if (outlined.length > 0) {
    const n = sum(issues, (i) => i.kind === 'outlined_text');
    defects.push(
      defect('text_as_outlines', n, `${n} small closed paths where characters were expected`, outlined[0]!.pageIndex),
    );
  }

  const curves = sum(issues, (i) => i.kind === 'unsupported_curve');
  if (curves > 0) {
    defects.push(defect('unsupported_curves', curves, `${curves} curve segments that are not the canonical arc construction`));
  }

  const fonts = issues.filter((i) => i.kind === 'font_not_embedded');
  if (fonts.length > 0) {
    const names = fonts.map((i) => i.detail ?? '').join('; ');
    defects.push(defect('font_not_embedded', fonts.length, names || `${fonts.length} fonts named but not carried`));
  }

  const invisible = sum(issues, (i) => i.kind === 'invisible_text');
  if (invisible > 0) {
    defects.push(defect('text_layer_over_scan', invisible, `${invisible} text runs drawn in an invisible render mode`));
  }

  const writeOrder = issues.filter(
    (i) => i.kind === 'merge_candidate' && WROTE_THEM_APART.test(i.detail ?? ''),
  );
  if (writeOrder.length > 0) {
    const n = writeOrder.reduce((t, i) => t + i.count, 0);
    defects.push(
      defect(
        'text_write_order',
        n,
        `${n} text groupings the geometry would have merged and the producer's write order kept apart`,
      ),
    );
  }

  return report(defects);
}

/**
 * A file that never opened, in the same shape.
 *
 * The board's error codes classify *why* the read failed; the defects classify
 * what the person holding the drawing should do about it, and the two do not
 * map one to one. `PDF_LIMIT_EXCEEDED` covers a file that is too big and a
 * document with too many pages; `EXTRACTOR_FAILURE` is a bucket. What matters
 * is that encryption never arrives as corruption, because the two have
 * completely different fixes and sharing a message would send half the callers
 * to the wrong one.
 */
const FAILURE_DEFECT: Record<ErrorCode, DefectKind> = {
  PDF_ENCRYPTED: 'encrypted',
  PDF_UNSUPPORTED_MEDIA_TYPE: 'not_a_pdf',
  PDF_LIMIT_EXCEEDED: 'too_large',
  EXTRACTION_TIMEOUT: 'timed_out',
  PDF_CORRUPT: 'corrupt',
  EXTRACTOR_FAILURE: 'corrupt',
  EXTRACTION_UNSUPPORTED: 'corrupt',
  DRAWING_SOURCE_NOT_FOUND: 'corrupt',
  DRAWING_SOURCE_FORBIDDEN: 'corrupt',
};

/**
 * Structural rather than by class, so `validate/` does not depend on `extract/`.
 * The extractor's error satisfies it; so does anything else that classifies a
 * failure with a code from the closed set.
 */
export interface ClassifiedFailure {
  code: ErrorCode;
  message: string;
}

export function validateFailure(error: ClassifiedFailure): ValidationReport {
  const kind = FAILURE_DEFECT[error.code] ?? 'corrupt';
  // The extractor's message carries the numbers — the byte count, the page
  // count, the budget — and throwing them away to re-say the same thing in
  // rounder words would be losing the only measurement there is.
  return report([defect(kind, 1, error.message)]);
}
